import { http } from './http';
import type { HttpResponse } from './http';
import { secureServer } from './AppSettings';

export const cartService = {
    getProduct,
    getDiscount,
    checkout,
    checkoutTest,
    isBlackListed,
    getIndustrialProducts,
    getIndustrialRawCart,
    createCheckout,
    createPayPalOrder,
    capturePayPalOrder,
};

// Response from /cart/product
export interface CartProductResponse {
    productId: number;
    productName: string;
    areaProduct: string;
    licenseId: number;
    licenseName: string;
    price: number;
    exRate: number;
    qty: number;
    amount: number;
    includedInDataStudio: boolean;
    includedProductName: string;
    productShort: string;
    showPromo: boolean;
    promoProductId: number;
    promoProductName: string;
    promoLicenseId: number;
    promoLicenseName: string;
    promoPrice: number;
    promoQty: number;
    promoAmount: number;
    promoDiscount: number;
    promoSavings: number;
}

// stored in cart
export interface CartProduct {
    productId: number;
    productName: string;
    licenseId: number;
    licenseName: string;
    price: number;
    qty: number;
    amount: number;
}

// tax info in the cart
export interface OrderTax {
    name: string;
    amount: number;
}

// local cart info
export interface CartInfo {
    products: CartProduct[];
    discountCode: string;
    taxes: Array<OrderTax>;
    discount: number;
    total: number;
}

export interface CartUpdateResponse {
    pid: number;
    lic: number;
    qty: number;
}

export interface CartDiscount {
    discount: number;
    resellerId: number;
}

export interface CartCheckoutRequest {
    bill_first_name: string;
    bill_last_name: string;
    bill_company: string;
    bill_address_one: string;
    bill_city: string;
    bill_state_or_province: string;
    bill_country: string;
    bill_postal_code: string;
    bill_email: string;
    bill_phone: string;
    bill_fax?: string;
    bill_po?: string;
    credit_card_brand: string;
    credit_card_number: string;
    expire_month: string;
    expire_year: string;
    cvv: string;
    discount_code?: string; // Used by QuoteCheckout only (no checkout session for quotes)
    comments?: string; // Used by QuoteCheckout only (memo for annual support quotes)
    reseller_id?: number;
    [key: string]: string | number;
}

export interface CartCheckoutResponse {
    status: boolean;
    order: string;
    message: string;
}

export interface BlackListedRequest {
    name: string;
    email: string;
    creditCard: string;
}

export interface BlackListedResponse {
    status: boolean;
    message: string;
}

// --- Checkout Session ---

export interface CheckoutCreateRequest {
    existingOrderId?: string;
    upgradeFromOrderId?: string;
    clientId?: number;
    type: string;
    discountCode?: string;
    itemsJson: string;
    billingJson: string;
    shippingJson?: string;
    comments?: string;
    industrial?: IndustrialCheckoutData;
    // Upgrade flow: token "{orderId}-{pid}-{lic}-{qty}-{pid_from}". When set,
    // the server re-runs GetUpgradeInfo and ignores itemsJson / discountCode.
    upgradeToken?: string;
}

export interface IndustrialCheckoutData {
    cart: IndustrialCartEntry[];
    supportYears: number;
    shippingRegion: string; // na | eu | au | none
}

export interface IndustrialCartEntry {
    isSoftware: boolean;
    // software fields
    slots?: number;
    liveUsb?: boolean;
    // hardware fields
    model?: string; // DESKTOP | SYSTEM
    bay?: string;
    mon?: string;
    prn?: string[];
    ada?: string[];
}

export interface CheckoutCreateResult {
    token: string;
    amountUSD: number;
    tax: number;
    pst: number;
    discountCode: string;
    discountAmount: number;
    currency: string;
    exRate: number;
    createdAt: string;
}

// --- PayPal REST API ---

export interface PayPalCreateOrderResponse {
    payPalOrderId: string;
    checkoutToken: string;
}

export interface PayPalCaptureOrderResponse {
    success: boolean;
    orderId?: string;
    message?: string;
}

// --- Industrial Cart ---

// response from : API-SERVER/api/cart/sessions?sid=abcdef123456
// also stored in industrial cart
export interface IndustrialProduct {
    id: number;
    product: string;
    licId: number;
    license: string;
    slots?: number;
    price: number;
    qty: number;
    amt: number;
}

export interface IndustrialCartInfo {
    products: IndustrialProduct[];
    taxes: Array<OrderTax>;
    total: number;
}
// --- END of Industrial Cart ---

async function getProduct(pid: number, lic: number, qty: number): Promise<HttpResponse<CartProductResponse>> {
    const path = `/cart/product?pid=${pid}&lic=${lic}&qty=${qty}`;
    const result = await http<CartProductResponse>({
        path: path,
    });
    return result;
}

async function getDiscount(
    subtotal: number,
    discountCode: string,
    pid: number,
    lic: number,
    qty: number
): Promise<CartDiscount | null> {
    const path = `/cart/discount?subtotal=${subtotal}&discountCode=${discountCode}&pid=${pid}&lic=${lic}&qty=${qty}`;
    const result = await http<CartDiscount>({
        path: path,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return null;
    }
}

async function checkout(params: CartCheckoutRequest): Promise<HttpResponse<CartCheckoutResponse>> {
    return http<CartCheckoutResponse, any>({
        path: `${secureServer}/OrderProcessBackend.aspx`,
        method: 'POST',
        body: params,
        formData: true,
        skipAuth: true,
        timeoutMs: 60000,
    });
}

async function checkoutTest(params: CartCheckoutRequest): Promise<CartCheckoutResponse | null> {
    const result = await http<CartCheckoutResponse, any>({
        path: `${secureServer}/OrderProcessBackendTest.aspx`,
        method: 'POST',
        body: params,
        formData: true,
    });
    if (result.ok && result.body) {
        return result.body;
    } else {
        return null;
    }
}

async function isBlackListed(request: BlackListedRequest): Promise<BlackListedResponse | null> {
    const result = await http<BlackListedResponse, BlackListedRequest>({
        path: `/cart/blacklisted`,
        method: 'POST',
        body: request,
    });

    if (result.ok) {
        return result.body;
    } else {
        return null;
    }
}

async function getIndustrialProducts(sid: string): Promise<HttpResponse<IndustrialProduct[]>> {
    const result = await http<IndustrialProduct[]>({
        path: `/cart/sessions?sid=${sid}`,
    });
    return result;
}

async function getIndustrialRawCart(sid: string): Promise<HttpResponse<IndustrialCheckoutData>> {
    const result = await http<IndustrialCheckoutData>({
        path: `/cart/sessions/raw?sid=${sid}`,
    });
    return result;
}

// Returns the raw HttpResponse so callers can surface the server's real
// failure reason (result.message) instead of a generic dialog string.
async function createCheckout(params: CheckoutCreateRequest): Promise<HttpResponse<CheckoutCreateResult>> {
    return await http<CheckoutCreateResult, CheckoutCreateRequest>({
        path: `/cart/checkout`,
        method: 'POST',
        body: params,
    });
}

// Returns the raw HttpResponse so callers can surface the server's real
// failure reason (result.message) instead of a generic dialog string.
async function createPayPalOrder(params: CheckoutCreateRequest): Promise<HttpResponse<PayPalCreateOrderResponse>> {
    return await http<PayPalCreateOrderResponse, CheckoutCreateRequest>({
        path: `/cart/paypal/create-order`,
        method: 'POST',
        body: params,
    });
}

// Returns the raw HttpResponse so callers can surface the server's real
// failure reason (result.message) instead of a generic dialog string.
async function capturePayPalOrder(
    payPalOrderId: string,
    checkoutToken: string
): Promise<HttpResponse<PayPalCaptureOrderResponse>> {
    return await http<PayPalCaptureOrderResponse, { payPalOrderId: string; checkoutToken: string }>({
        path: `/cart/paypal/capture-order`,
        method: 'POST',
        body: { payPalOrderId, checkoutToken },
    });
}
